/**
 * The message-editor `handleChange` of the per-lead campaign overview, as pure steps.
 *
 * The page renders one editor per channel and each carried a verbatim copy of the same
 * forty-line handler; the steps live here so the page composes them once and a change to the
 * edit semantics (which field, which node, when the campaign is dirty) happens in one place.
 */

/** Which text an edit targets: the event-branch alternative while that branch is shown. */
export type NodeMessageField = "message" | "alternateMessage";

export const resolveNodeMessageField = (eventBasedState: boolean): NodeMessageField =>
	eventBasedState ? "alternateMessage" : "message";

interface MessageNode {
	id: string;
	data: object;
}

/**
 * The node list with the selected node's message field replaced. Every other node keeps its
 * reference, so ReactFlow re-renders only the edited one.
 */
export const replaceNodeMessage = <N extends MessageNode>(
	nodes: N[],
	selectedNodeId: string | undefined,
	field: NodeMessageField,
	value: string
): N[] =>
	nodes.map((node) =>
		node.id === selectedNodeId ? { ...node, data: { ...node.data, [field]: value } } : node
	);

interface SelectedMessageNode {
	data?: Partial<Record<NodeMessageField, string>>;
}

/** Whether the edit differs from what the selected node already holds in that field. */
export const isNodeMessageChanged = (
	selectedNode: SelectedMessageNode | null | undefined,
	field: NodeMessageField,
	value: string
): boolean => value !== selectedNode?.data?.[field];
